/*!
 * Portal JS
 */
import {
  clamp,
  offsetLatLngByMeters,
  bearingBetweenLatLng,
  angleDiffDeg,
  axialDiffDeg,
  normalizeBearingDeg,
  roundToTenth,
  toFiniteNumber,
  interpolateByRatioNullable
} from './portal-geo-utils.js'
import {
  pathWithoutNamespacePrefixes,
  pathWithoutLeadingScope,
  pathWithoutNumericParts,
  getPathLeafField,
  toUnixMs,
  formatUnixMsForDisplay,
  parseDisplayTimestampToUnixMs,
  upsertDocumentWpmlTimestamp,
  readDocumentWpmlTimestampMs,
  getMissionCentroidLatLng,
  normalizeMissionBaseName,
  fetchNearestSettlementName,
  buildLocationBasedMissionBaseName,
  buildLayerStyle
} from './portal-mini-utils.js'
import {
  parseKmlMission,
  parseKmlMissionWithProgress
} from './portal-kml-utils.js'
import {
  ensureTooltip,
  ensureDropdown,
  applyMod2ShortcutTooltips,
  isTypingContext,
  parseShortcut,
  normalizeEventKey,
  eventMatchesShortcut
} from './portal-shortcut-utils.js'
import {
  renderMarkdownToHtml,
  renderReadmePanel
} from './portal-readme-utils.js'
import {
  I18N_HU
} from './portal-i18n-hu.js'
import {
  PORTAL_PRESETS
} from './portal-presets.js'
import {
  DEM_PROVIDER_CONFIG,
  DEM_PROVIDER_PRIORITY,
  DEM_CURSOR_PROVIDER_PRIORITY,
  DEM_PROVIDER_G,
  DEFAULT_MAP_CURSOR_ELEVATION_PROVIDER,
  isDemProviderEnabledFromConfig,
  MAP_CURSOR_ELEVATION_CACHE_MAX,
  MAP_CURSOR_ELEVATION_COORD_DECIMALS,
  MAP_CURSOR_ELEVATION_MIN_REQUEST_INTERVAL_MS
} from './portal-dem-providers.js'
import {
  fnGetKmlActionsFromProps,
  fnIsKmlActionPath,
  fnSetKmlActionsInProps,
  fnSetPointKmlActions,
  fnBuildSharedKmlActionsForPoints,
  fnGetKmzActionGroupsFromProps,
  fnIsKmzActionPath,
  fnSetKmzActionGroupsInProps,
  fnSetPointKmzActionGroups,
  fnApplyKmzActionFileSuffixDisplayModeToGroups,
  fnResolveKmzActionFileSuffixPointNameTokensInGroups,
  fnSyncPointActionFileSuffixWithName,
  fnBuildSharedKmzActionGroupsForPoints,
  fnToBoolLike
} from './portal-action-state.js'
import {
  normalizeKmzWaypointForExport,
  normalizeKmzMissionLevelForExport,
  buildKmzCanonicalExportDocs,
  parseKmzMission,
  parseKmzMissionWithProgress
} from './portal-kmz-mission-utils.js'
import {
  renderPortalPropEditorRows
} from './portal-point-edit-ui.js'
import {
  createPortalPointEditLaunchers
} from './portal-point-edit-launchers.js'
import {
  createPortalPropRuntime,
  buildRenderPropEditorRowsDeps,
  buildPointEditLauncherOptions
} from './portal-prop-runtime.js'
import {
  showPortalPropContextMenu
} from './portal-prop-context-bridge.js'
import * as portalAltitudeUtils from './portal-altitude-utils.js'
import * as portalPointMapUtils from './portal-point-map-utils.js'
import * as portalImportStateUtils from './portal-import-state-utils.js'
import * as portalWaypointCoreUtils from './portal-waypoint-core-utils.js'
import {
  renderImportLines as renderImportLinesRaw,
  setMissionWaypointLatLng as setMissionWaypointLatLngRaw,
  placeMissionWaypointsInViewport as placeMissionWaypointsInViewportRaw,
  loadMissionFromParsedMission as loadMissionFromParsedMissionRaw,
  closeEditedMission as closeEditedMissionRaw,
  closeMissionVariants as closeMissionVariantsRaw,
  restoreImportDraftFromIndexedDb as restoreImportDraftFromIndexedDbRaw,
  exportCurrentMission as exportCurrentMissionRaw
} from './portal-mission-render.js'
import {
  escapeHtmlLite,
  runPointHeightDemOverwriteFlow
} from './portal-height-tools.js'
import {
  cacheDataKey,
  refreshGeoTiffCacheMaxBytes,
  cacheReadIndex,
  cacheWriteIndex,
  cacheTouch,
  cacheLoad,
  cacheStore
} from './portal-geotiff-cache.js'
import {
  loadGeoTiffFile
} from './portal-geotiff-ui.js'
import {
  initPortalLoadingWidget
} from './portal-loading-widget.js'
import {
  openMapCursorElevationSettingsModal
} from './portal-elevation-ui.js'
import {
  openProjectSettingsEditor
} from './portal-project-settings.js'
import {
  bindCoreUiHandlers
} from './portal-ui-handlers.js'
import {
  createRoute3dSyncController
} from './portal-route3d-sync.js'
import {
  createPortalInfoController,
  bindInfoModalHandler
} from './portal-info-version.js'
import {
  createSeekTools
} from './portal-seek-tools.js'
import {
  bindMissionToolbarHandlers
} from './portal-mission-toolbar.js'
import {
  bindWaypointToolsHandlers
} from './portal-waypoint-tools-ui.js'
import {
  createLayerParsers
} from './portal-layer-parsers.js'
import {
  createLayerLoaderController
} from './portal-layer-loader.js'
import {
  createLayerCoreController
} from './portal-layer-core.js'
import {
  initializePortalMap
} from './portal-map-init.js'
import {
  createMyLocationController
} from './portal-my-location.js'
import {
  createMapCursorElevationController
} from './portal-map-cursor-elevation.js'
import {
  createDemBatchController,
  fetchSurfaceElevationByProvider
} from './portal-dem-batch.js'
import {
  bindSeekUiHandlers
} from './portal-seek-ui.js'
import {
  deactivateMod2ModesUi,
  initMod2KeyboardShortcutsUi,
  initMobileNavbarArrowsUi
} from './portal-mod2-ui.js'
import {
  bindPolygonCreateClick,
  bindAltitudeUiHandlers,
  bindQuickActionHandlers,
  bindExternalLinkInterceptor,
  bindBootstrapOverlayUiHandlers,
  bindBeforeUnloadCleanupHandler
} from './portal-ui-mini.js'
import { runPortalGeneral } from './portal-general.js'
import { createPortalPropContextMenu } from './portal-prop-context-ui.js'

initPortalLoadingWidget()

const runPortalGeneralPromise = runPortalGeneral({
  $: jQuery,
  _: _,
  L: window.L,
  clamp,
  offsetLatLngByMeters,
  bearingBetweenLatLng,
  angleDiffDeg,
  axialDiffDeg,
  normalizeBearingDeg,
  roundToTenth,
  toFiniteNumber,
  interpolateByRatioNullable,
  pathWithoutNamespacePrefixes,
  pathWithoutLeadingScope,
  pathWithoutNumericParts,
  getPathLeafField,
  toUnixMs,
  formatUnixMsForDisplay,
  parseDisplayTimestampToUnixMs,
  upsertDocumentWpmlTimestamp,
  readDocumentWpmlTimestampMs,
  getMissionCentroidLatLng,
  normalizeMissionBaseName,
  fetchNearestSettlementName,
  buildLocationBasedMissionBaseName,
  buildLayerStyle,
  parseKmlMission,
  parseKmlMissionWithProgress,
  ensureTooltip,
  ensureDropdown,
  applyMod2ShortcutTooltips,
  isTypingContext,
  parseShortcut,
  normalizeEventKey,
  eventMatchesShortcut,
  renderMarkdownToHtml,
  renderReadmePanel,
  I18N_HU,
  PORTAL_PRESETS,
  DEM_PROVIDER_CONFIG,
  DEM_PROVIDER_PRIORITY,
  DEM_CURSOR_PROVIDER_PRIORITY,
  DEM_PROVIDER_G,
  DEFAULT_MAP_CURSOR_ELEVATION_PROVIDER,
  fnIsDemProviderEnabled: function (providerKey) {
    return isDemProviderEnabledFromConfig(providerKey, DEM_PROVIDER_CONFIG)
  },
  MAP_CURSOR_ELEVATION_CACHE_MAX,
  MAP_CURSOR_ELEVATION_COORD_DECIMALS,
  MAP_CURSOR_ELEVATION_MIN_REQUEST_INTERVAL_MS,
  fnGetKmlActionsFromProps,
  fnIsKmlActionPath,
  fnSetKmlActionsInProps,
  fnSetPointKmlActions,
  fnBuildSharedKmlActionsForPoints,
  fnGetKmzActionGroupsFromProps,
  fnIsKmzActionPath,
  fnSetKmzActionGroupsInProps,
  fnSetPointKmzActionGroups,
  fnApplyKmzActionFileSuffixDisplayModeToGroups,
  fnResolveKmzActionFileSuffixPointNameTokensInGroups,
  fnSyncPointActionFileSuffixWithName,
  fnBuildSharedKmzActionGroupsForPoints,
  fnToBoolLike,
  normalizeKmzWaypointForExport,
  normalizeKmzMissionLevelForExport,
  buildKmzCanonicalExportDocs,
  parseKmzMission,
  parseKmzMissionWithProgress,
  renderPortalPropEditorRows,
  createPortalPropRuntime,
  buildRenderPropEditorRowsDeps,
  buildPointEditLauncherOptions,
  createPortalPointEditLaunchers,
  showPortalPropContextMenu,
  portalAltitudeUtils,
  portalPointMapUtils,
  portalImportStateUtils,
  portalWaypointCoreUtils,
  renderImportLinesRaw,
  setMissionWaypointLatLngRaw,
  placeMissionWaypointsInViewportRaw,
  loadMissionFromParsedMissionRaw,
  closeEditedMissionRaw,
  closeMissionVariantsRaw,
  restoreImportDraftFromIndexedDbRaw,
  exportCurrentMissionRaw,
  escapeHtmlLite,
  runPointHeightDemOverwriteFlow,
  cacheDataKey,
  refreshGeoTiffCacheMaxBytes,
  cacheReadIndex,
  cacheWriteIndex,
  cacheTouch,
  cacheLoad,
  cacheStore,
  loadGeoTiffFile,
  openMapCursorElevationSettingsModal,
  openProjectSettingsEditor,
  bindCoreUiHandlers,
  createRoute3dSyncController,
  createPortalInfoController,
  bindInfoModalHandler,
  createSeekTools,
  bindMissionToolbarHandlers,
  bindWaypointToolsHandlers,
  createLayerParsers,
  createLayerLoaderController,
  createLayerCoreController,
  initializePortalMap,
  createMyLocationController,
  createMapCursorElevationController,
  createDemBatchController,
  fetchSurfaceElevationByProvider,
  bindSeekUiHandlers,
  deactivateMod2ModesUi,
  initMod2KeyboardShortcutsUi,
  initMobileNavbarArrowsUi,
  bindPolygonCreateClick,
  bindAltitudeUiHandlers,
  bindQuickActionHandlers,
  bindExternalLinkInterceptor,
  bindBootstrapOverlayUiHandlers,
  bindBeforeUnloadCleanupHandler,
})

void runPortalGeneralPromise.then(function (portalGeneralApi) {
  if (!portalGeneralApi || typeof portalGeneralApi !== 'object') return
  const showPropContextMenu = createPortalPropContextMenu({
    fnT: portalGeneralApi.fnT
  })

  window.Swal = portalGeneralApi.baseSwal
  window.PORTAL_IS_DEM_PROVIDER_ENABLED = portalGeneralApi.fnIsDemProviderEnabled
  window.GOOGLE_ELEVATION_API_KEY = portalGeneralApi.googleElevationApiKey
  window.PORTAL_BASE_SWAL = portalGeneralApi.baseSwal
  window.PORTAL_AREA_SWAL = portalGeneralApi.areaSwal
  window.PORTAL_T = portalGeneralApi.fnT
  window.PORTAL_SHOW_PROP_CONTEXT_MENU = showPropContextMenu
  window.PORTAL_RESOLVE_PROP_VALUE_HELP = portalGeneralApi.fnResolvePropValueHelp
  window.PORTAL_GET_DEM_PROVIDER_LABEL = portalGeneralApi.fnGetDemProviderLabel
  window.PORTAL_DEM_PROVIDER_PRIORITY = Array.isArray(DEM_PROVIDER_PRIORITY) ? DEM_PROVIDER_PRIORITY.slice() : []
  window.PORTAL_GET_MAP_CURSOR_ELEVATION_PROVIDER = portalGeneralApi.fnGetMapCursorElevationProvider
  window.PORTAL_SET_MAP_CURSOR_ELEVATION_PROVIDER = portalGeneralApi.fnSetMapCursorElevationProvider
  window.PORTAL_BUILD_MAP_CURSOR_ELEVATION_TRY_ORDER = portalGeneralApi.fnBuildMapCursorElevationTryOrder
  window.PORTAL_SCHEDULE_ROUTE3D_SHARED_SYNC = portalGeneralApi.fnScheduleRoute3dSharedSync
  window.PORTAL_ESCAPE_HTML_LITE = portalGeneralApi.fnEscapeHtmlLite
}).catch(function (error) {
  console.error('portal-general initialization failed', error)
})
